<script lang="ts" setup>
import type { RouteRecordName } from 'vue-router';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import type {
  PlayerTrackingHistoryFormData,
  PlayerTrackingHistoryPayload,
} from '@modules/players/submodules/PlayerProfile/types';
import VLoader from '@components/loader/VLoader.vue';
import PlayersTrackingForm
  from '@modules/players/submodules/PlayerProfile/components/PlayersTrackingForm/PlayersTrackingForm.vue';
import { useTrackingHistoryEditMutation } from '@modules/players/submodules/PlayerProfile/mutations';
import { usePlayerTrackingHistoryQuery } from '@modules/players/submodules/PlayerProfile/queries';
import {
  TRANSLATION_GROUP_PLAYER_TRACKER,
} from '@modules/players/submodules/PlayerProfile/submodules/PlayerTracker/constants';
import { RESPONSE_DATE_FORMAT } from '@modules/smart-filters/constants';
import { formatDate } from '@vueuse/core';

defineProps<{ backRoute?: RouteRecordName }>();

const route = useRoute();

const playerId = computed<string | undefined>(() => (route.params.id ? String(route.params.id) : undefined));
const historyId = computed<number | undefined>(() => (route.params.historyId ? Number(route.params.historyId) : undefined));
const merchantId = computed<number | undefined>(() => (route.params.merchant ? Number(route.params.merchant) : undefined));

const {
  data: history,
  isLoading: isHistoryLoading,
} = usePlayerTrackingHistoryQuery(playerId, historyId, merchantId);

const {
  mutate: onSubmitHandler,
  errors,
  clearError,
  isLoading: isHistoryMutateLoading,
} = useTrackingHistoryEditMutation();

function onSubmit(payload: PlayerTrackingHistoryFormData): void {
  const data: PlayerTrackingHistoryPayload = {
    history_of_players_change: {
      ...payload,
      date: formatDate(payload.date, RESPONSE_DATE_FORMAT),
    },
  };

  if (!playerId.value || !historyId.value || !merchantId.value) {
    return;
  }

  onSubmitHandler({
    data,
    playerId: playerId.value,
    historyId: historyId.value,
    merchant: merchantId.value,
  });
}
</script>

<template>
  <VLoader v-data-qa-auto-id="'PlayerTrackingHistoryEditPage'"
    :loading="isHistoryLoading"
    mode="overlay"
  >
    <div class="p-5">
      <h4 class="p-card-title mb-3 text-lg">
        {{ $getTranslation(TRANSLATION_GROUP_PLAYER_TRACKER, 'HISTORY_CHANGES_PLAYER') }}
      </h4>
      <PlayersTrackingForm
        :back-btn-label="$getTranslation(TRANSLATION_GROUP_PLAYER_TRACKER, 'HISTORY_CHANGES_CLOSE')"
        :back-btn-route="backRoute"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_PLAYER_TRACKER, 'HISTORY_CHANGES_SAVE')"
        :errors="errors"
        :serial-id-label="$getTranslation(TRANSLATION_GROUP_PLAYER_TRACKER, 'HISTORY_CHANGES_SERIAL_ID')"
        :serial-id-value="history?.history_of_players_change.serial_id"
        :an-id-label="$getTranslation(TRANSLATION_GROUP_PLAYER_TRACKER, 'HISTORY_CHANGES_AN_ID')"
        :an-id-value="history?.history_of_players_change.anid"
        :date-label="$getTranslation(TRANSLATION_GROUP_PLAYER_TRACKER, 'HISTORY_CHANGES_DATE')"
        :date-value="history?.history_of_players_change.date"
        :is-loading="isHistoryMutateLoading"
        @submit="onSubmit"
        @interacted="clearError"
      />
    </div>
  </VLoader>
</template>
